import React, { useState } from 'react'
import axios from 'axios'
import { useApiBase } from './api-base-provider'

/**
 * Shown when the selected API target is a remote deployment we have no valid
 * token for. Logs in against that deployment directly — deliberately using bare
 * axios rather than useApiClient(), whose 401 interceptor is what opens this.
 */
export function RemoteLoginDialog() {
  const {
    baseUrl, isRemote,
    remoteLoginNeeded, setRemoteLoginNeeded,
    setRemoteToken, setBaseUrl,
  } = useApiBase()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isRemote || !remoteLoginNeeded) return null

  const handleLogin = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios.post(`${baseUrl}/api/login`, { email, password })
      if (!data.token) return setError('No token received from server.')
      setRemoteToken(data.token)
      setRemoteLoginNeeded(false)
      setEmail('')
      setPassword('')
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.response?.data ?? err?.message
      setError(typeof msg === 'string' ? msg : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  // Cancelling means giving up on the remote — staying pointed at it would just
  // re-open this dialog on the next 401.
  const handleCancel = () => {
    setRemoteLoginNeeded(false)
    setRemoteToken(null)
    setBaseUrl('')
  }

  return (
    <div className="v2-dialog-overlay">
      <div className="v2-dialog" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="v2-dialog-header">
          <div className="v2-dialog-title">Log in to remote target</div>
        </div>

        <div className="v2-dialog-body">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', marginBottom: 16,
            borderRadius: 'var(--v2-radius-md)',
            background: 'var(--v2-warning-soft)',
            color: 'var(--v2-warning)',
            fontSize: 12,
          }}>
            <span className="v2-api-dot v2-api-dot--remote" />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {baseUrl}
            </span>
          </div>

          <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', lineHeight: 1.6, marginTop: 0 }}>
            Enter your credentials for this deployment.
          </p>

          {error && (
            <div style={{
              padding: '8px 12px', marginBottom: 12,
              borderRadius: 'var(--v2-radius-md)',
              background: 'var(--v2-danger-bg, rgba(220, 38, 38, 0.1))',
              color: 'var(--v2-danger, #dc2626)',
              fontSize: 12,
            }}>
              {error}
            </div>
          )}

          <div className="v2-field">
            <label className="v2-label">Email</label>
            <input
              className="v2-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@example.com"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>

          <div className="v2-field">
            <label className="v2-label">Password</label>
            <input
              className="v2-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>
        </div>

        <div className="v2-dialog-footer">
          <button className="v2-btn v2-btn--ghost" onClick={handleCancel}>
            Cancel
          </button>
          <button
            className="v2-btn v2-btn--primary"
            onClick={handleLogin}
            disabled={loading || !email.trim() || !password.trim()}
          >
            {loading ? 'Logging in...' : 'Log in'}
          </button>
        </div>
      </div>
    </div>
  )
}
